import { Avatar, Icon } from '@dixa/lib-shared-habla';
import type { SearchResult, SearchScope } from 'lib-search-types';
import type { SearchFilterItem } from 'lib-search-ui';
import { convertChannelCasing } from 'lib-shared-api-utils';

import { channelAttributes, getChannelName } from 'utils/channels';
import { getUserDisplayName } from 'utils/user';

/**
 * Maps a search result to a filter item for every scope.
 *
 * Each scope picks the fields that identify and label its resource — adding a new scope to
 * `SearchScope` requires adding its mapper here.
 */
type SearchScopeItemMappers = {
  readonly [TScope in SearchScope]: (
    result: SearchResult<TScope>['result'],
  ) => SearchFilterItem;
};

const itemMappers: SearchScopeItemMappers = {
  channels: (channel) => {
    const label = getChannelName(channel);

    return {
      value: convertChannelCasing(channel, 'truncate'),
      label,
      icon: channelAttributes[channel].icon,
    };
  },

  companies: (company) => ({
    value: company.id,
    label: company.name,
  }),

  conversations: (conversation) => ({
    value: `${conversation.id}`,
    label: `#${conversation.id}`,
  }),

  queues: (queue) => ({
    value: queue.id,
    label: queue.name,
  }),

  tags: (tag) => ({
    value: tag.id,
    label: tag.name,
    icon: (
      <Icon name="circle-filled" color={tag.color ?? undefined} size={12} />
    ),
  }),

  teams: (team) => ({
    value: team.id,
    label: team.name,
  }),

  users: (user) => {
    const label = getUserDisplayName(user);

    const description =
      user.type === 'contact'
        ? user.email || user.phone_number || undefined
        : undefined;

    return {
      value: user.id,
      label,
      description: description === label ? undefined : description,
      icon: <Avatar user={user} size="24" />,
    };
  },
};

/**
 * Maps a search result to the filter item rendered in the list, using the
 * mapper of the given scope.
 *
 * @param scope The search scope the result belongs to.
 * @param result The search result to map.
 */
export function mapSearchResultToFilterItem<TScope extends SearchScope>(
  scope: TScope,
  result: SearchResult<TScope>['result'],
): SearchFilterItem {
  return itemMappers[scope](result);
}
